import React, { ComponentType } from 'react';
import { ActivityIcon, BoxesIcon, PhoneIcon, RadioIcon, RefreshCwIcon, SunIcon, ThermometerIcon, WrenchIcon, ZapIcon } from 'lucide-react';
import { ARCHIVE_CONTACT, archiveCategories } from './archiveData';
const icons: Record<string, ComponentType<{
  className?: string;
}>> = {
  'class-iv-laser': ZapIcon,
  shockwave: ActivityIcon,
  heit: RadioIcon,
  led: SunIcon,
  thermal: ThermometerIcon,
  'peptides-stem-cells': ActivityIcon,
  'used-equipment': RefreshCwIcon,
  concierge: BoxesIcon,
  'neuro-reformer': WrenchIcon
};
const categoryAnchorIds: Record<string, string> = {
  'class-iv-laser': 'category-class-iv-laser',
  shockwave: 'category-shockwave',
  heit: 'category-high-energy-inductive-therapy',
  led: 'category-whole-body-red-light-led-therapy',
  thermal: 'category-thermal-imaging',
  'peptides-stem-cells': 'category-peptides-stem-cells',
  'used-equipment': 'category-used-equipment',
  concierge: 'category-concierge-modality-suite',
  'neuro-reformer': 'category-neuro-reformer'
};
function EquipmentPanel({
  id,
  Icon
}: {
  id: string;
  Icon: ComponentType<{
    className?: string;
  }>;
}) {
  const label = id === 'used-equipment' ? 'PRE-OWNED' : id === 'concierge' ? 'CURATED SYSTEM' : 'TECHNOLOGY';
  return <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden border-b border-slate-100 bg-slate-50" data-id="element-26" data-mp="sup0ah09ymx3">
      <div className="absolute inset-x-8 top-1/2 h-px bg-teal/25" data-id="element-27" data-mp="otmvzlri3ko9" />
      <div className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full border border-navy/10" data-id="element-28" data-mp="k0kbs48m5nkq" />
      <div className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-teal/40" data-id="element-29" data-mp="f2s8yf6fkh1r" />
      <div className="absolute flex h-20 w-28 items-center justify-center rounded-2xl border border-navy/15 bg-white shadow-[0_12px_28px_-18px_rgba(26,27,75,0.45)]" data-id="element-30" data-mp="54be374dfiaf">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-navy text-teal-light" data-id="element-31" data-mp="13j4udvi1de5">
          <Icon className="h-7 w-7" data-id="element-32" data-mp="3mlp39j8qr5tr" />
        </div>
      </div>
      <span className="absolute bottom-5 rounded-full border border-navy/10 bg-white/80 px-2.5 py-1 text-[10px] font-semibold tracking-[0.16em] text-navy/55" data-id="element-33" data-mp="3rmd1aove0mxh">
        {label}
      </span>
      <span className="absolute left-8 top-7 h-2 w-2 rounded-full bg-teal" data-id="element-34" data-mp="3ikwvkufip8zl" />
      <span className="absolute right-9 top-10 h-1.5 w-1.5 rounded-full bg-navy/30" data-id="element-35" data-mp="a5lefl35aruo" />
    </div>;
}
export function ArchiveCatalog() {
  return <section id="products" className="w-full bg-white py-20 sm:py-24" data-id="element-36" data-mp="e5x1f4izd49b">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-37" data-mp="gblk30h955i">
        <div className="max-w-2xl" data-id="element-38" data-mp="dpgqb4z13qz">
          <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal" data-id="element-39" data-mp="5ed5sr9n3aik">
            Product Categories
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-[2.4rem]" data-id="element-40" data-mp="t3pg5ugpdomg">
            Technologies to understand before you invest
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed text-charcoal/70" data-id="element-41" data-mp="178omadgfg3qc">
            Start with the category, then call JAG to compare product families,
            practical fit, training needs, and implementation considerations.
          </p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-42" data-mp="1c0zo5luq9zlj">
          {archiveCategories.map(category => {
          const Icon = icons[category.id] ?? ZapIcon;
          const categoryAnchorId = categoryAnchorIds[category.id];
          return <article id={categoryAnchorId} key={category.id} className="group flex h-full scroll-mt-[104px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_2px_20px_-8px_rgba(26,27,75,0.14)] transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-teal/40 hover:shadow-[0_18px_40px_-16px_rgba(26,27,75,0.25)]" data-id="element-43" data-mp="kbmsqf628c1m">
                {category.image ? <div className={`relative flex aspect-[4/3] items-center justify-center overflow-hidden border-b border-slate-100 bg-slate-50 ${category.imageFit === 'cover' ? '' : 'p-6'}`} data-id="element-44" data-mp="ea4aglnbhj58">
                    <img src={category.image} alt={category.imageAlt ?? category.name} className={`h-full w-full ${category.imageFit === 'cover' ? 'object-cover' : 'object-contain'}`} data-id="element-45" data-mp="s82otr1n1fkq" />
                  </div> : <EquipmentPanel id={category.id} Icon={Icon} data-id="element-46" data-mp="16wfb1waxbk21" />}
                <div className="flex flex-1 flex-col p-6" data-id="element-47" data-mp="1bw1x8v8qcamz">
                  <div className="flex items-start justify-between gap-3" data-id="element-48" data-mp="1bviifnedm2d6">
                    <div className="flex items-center gap-2.5" data-id="element-49" data-mp="1fyoi2c6zgvao">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal/10 text-teal" data-id="element-50" data-mp="1kwgmu6sq8bwa">
                        <Icon className="h-[18px] w-[18px]" data-id="element-51" data-mp="35fae6oe2lbyt" />
                      </span>
                      <h3 className="font-display text-[17px] font-bold leading-tight text-navy" data-id="element-52" data-mp="30fxozwzq7st2">
                        {category.name}
                      </h3>
                    </div>
                  </div>
                  <p className="mt-3 text-[13.5px] leading-relaxed text-charcoal/75" data-id="element-53" data-mp="2rfrg6oez3h08">
                    {category.detail}
                  </p>
                  <p className="mt-4 text-[11px] font-medium leading-relaxed text-slate-500" data-id="element-54" data-mp="3o4h1kopt6rqy">
                    {category.products.join(' · ')}
                  </p>
                  <a href={ARCHIVE_CONTACT.phoneHref} className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-navy transition-colors hover:text-teal focus-visible:outline-none focus-visible:text-teal" data-id="element-55" data-mp="3fbwxh8qqzuyq">
                    <PhoneIcon className="h-4 w-4" data-id="element-56" data-mp="6wvalz6ge75b" />
                    {category.ctaLabel ?? 'Call About This Category'}
                  </a>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}